/*
 * PW Identity — decode the stored pw:library token's claims client-side, so WHICH identity is
 * active is always readable at a glance instead of silent.
 *
 * Karen Klingborg incident, 2026-08-18: a stored token can point at the wrong customer (an
 * ambient Shopify-native session silently re-authenticates on any 401 — see pw-library.js's
 * trySessionBridge()), and nothing on the page said so — same layout, same styling, just
 * different (or empty) items. This exists to close that gap.
 *
 * No network call, no signature verification here — the server already verifies the token on
 * every real API call (packages/api/src/library.ts). This only reads the plaintext claims a
 * client already holds, the same trust boundary pw-library.js's own token handling already
 * accepts (it stores/reads the raw bearer token in localStorage today).
 *
 * Loaded from the site header (present on every page) BEFORE pw-library.js's section-scoped
 * script, but every consumer still guards with `window.PWIdentity &&` — same "optional dep,
 * graceful default" pattern the codebase already uses for window.PWAuth/window.PWFavorites.
 */
(function () {
  'use strict';
  var TOKEN_KEY = 'pw_library_token';

  /** base64url JWT payload segment -> its `email` claim, or null on ANY failure. Fails closed:
   *  a malformed/corrupt token must never throw and must never surface a wrong/garbage identity.
   *
   * tasks.db #816 (2026-09-01, PLAN v3 §2.3, Opus SF-8): delegates to window.PWAuth.tokenClaims
   * when it's loaded -- same optional-dep-with-graceful-default pattern this file's own
   * currentToken()/clear() already use for PWAuth. Two independent decoders of the SAME claim
   * were a real risk: PWAuth.tokenClaims used to be a raw atob+JSON.parse decode (fine for the
   * numeric claims it read then), and giving it an `email` claim on that path would mojibake any
   * non-ASCII address the moment #816's identity cross-check started reading it. Rather than fix
   * one and leave the other to drift again, tokenClaims adopted THIS file's already-correct
   * UTF-8-safe percent-decoding and became the one shared decoder. The percent-decode logic below
   * is kept only as the standalone fallback for a failed/not-yet-loaded pw-auth.js -- it must stay
   * in sync with tokenClaims' method (not just its answer) or this delegation silently doesn't
   * change anything of substance. */
  function decodeEmail(token) {
    if (!token || typeof token !== 'string') return null;
    if (window.PWAuth && window.PWAuth.tokenClaims) {
      var claims = window.PWAuth.tokenClaims(token);
      return claims && typeof claims.email === 'string' && claims.email ? claims.email : null;
    }
    var parts = token.split('.');
    if (parts.length !== 3) return null;
    try {
      var b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      var json = decodeURIComponent(
        atob(b64)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(''),
      );
      var payload = JSON.parse(json);
      return typeof payload.email === 'string' && payload.email ? payload.email : null;
    } catch (e) {
      return null;
    }
  }

  function currentToken() {
    try {
      return window.sessionStorage.getItem(TOKEN_KEY) || window.localStorage.getItem(TOKEN_KEY) || null;
    } catch (e) {
      return null;
    }
  }

  function currentEmail() {
    return decodeEmail(currentToken());
  }

  /** Clears the stored identity. Delegates to PWAuth's fuller clear (also drops
   *  pw_owned_handles + pw_favorites_last_merged_token) when it's loaded — same optional-dep
   *  delegation pattern pw-library.js's own clearToken() already uses. Falls back to clearing
   *  just the token this file itself reads, so the export still works standalone. */
  function clear() {
    if (window.PWAuth) return window.PWAuth.clearToken();
    try {
      window.sessionStorage.removeItem(TOKEN_KEY);
    } catch (e) {
      /* storage blocked */
    }
    try {
      window.localStorage.removeItem(TOKEN_KEY);
    } catch (e) {
      /* storage blocked */
    }
  }

  window.PWIdentity = { decodeEmail: decodeEmail, currentEmail: currentEmail, clear: clear };

  /**
   * Renders (or hides) one badge element. Absent/malformed identity -> hidden, never a guess.
   *
   * NO VISUAL GLYPH (2026-08-29, Paul's call): this used to render a raw 👤 emoji as the badge's
   * only visible content, which shows as a flat gray silhouette on non-Apple emoji fonts. Removed
   * entirely — no SVG icon, no initials avatar, both declined. Only the accessible affordance
   * remains: the button still hides/shows correctly, still navigates to My Library on click, and
   * still carries the full "Signed in as X..." label for assistive tech. The email itself lives
   * in a visually-hidden-but-DOM-present span — a standard accessible-name pattern, not a
   * workaround: a screen reader gets "Signed in as X" either way, and desktop CSS is free to
   * reveal it inline where the header actually has room (see the stylesheet block).
   */
  function renderBadge(el) {
    if (!el) return;
    var email = currentEmail();
    if (!email) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.hidden = false;
    el.textContent = '';
    var label = document.createElement('span');
    label.className = 'pw-identity__email';
    label.textContent = 'Signed in as ' + email;
    // 2026-09-03 (tasks.db #838, Paul live): the plaintext email reveal above stays in the DOM,
    // visually hidden, purely as a redundant accessible-text fallback — it is what was pushing the
    // cart icon off-screen when CSS made it visible on desktop, so nothing reveals it anymore.
    // Visible content is now this small avatar circle instead, matching the same
    // initials-in-a-circle treatment already shipped for the real account icon (Option D,
    // snippets/pw-account-avatar.liquid, 2026-08-30) — "we only need the initials in a circle
    // like on mobile." A JWT identity only ever carries an email, never a first/last name, so
    // this always uses the single-letter fallback that snippet already falls back to itself.
    var avatar = document.createElement('span');
    avatar.className = 'pw-identity__avatar pw-account-avatar';
    avatar.setAttribute('aria-hidden', 'true');
    avatar.textContent = email.charAt(0).toUpperCase();
    var avatarPalette = ['c44d6f', 'd4267e', '1d1b19'];
    var colorSum = 0;
    for (var i = 0; i < email.length; i++) colorSum += email.charCodeAt(i);
    avatar.style.width = '22px';
    avatar.style.height = '22px';
    avatar.style.background = '#' + avatarPalette[colorSum % avatarPalette.length];
    var switchBtn = document.createElement('button');
    switchBtn.type = 'button';
    switchBtn.className = 'pw-identity__switch';
    switchBtn.setAttribute('data-pw-identity-switch', '');
    switchBtn.setAttribute('aria-label', 'Signed in as ' + email + '. Open My Library to switch accounts.');
    switchBtn.addEventListener('click', function () {
      // Navigate only — NEVER clear from the header. One unlabeled tap must not sign the
      // customer out (2026-08-28, Paul hit exactly this on the release-theme preview: the old
      // clear()-then-navigate left him signed out before he reached a page that could tell him
      // what happened). My Library's own identity bar shows the email and owns the explicit
      // "Not you?" clear (pw-library.js) — that destructive step belongs one page further in,
      // behind a labeled control, not a bare tap on an unlabeled icon.
      window.location.href = '/pages/my-library';
    });
    el.appendChild(label);
    el.appendChild(avatar);
    el.appendChild(switchBtn);
  }

  function init() {
    var badges = document.querySelectorAll('[data-pw-identity-badge]');
    for (var i = 0; i < badges.length; i++) renderBadge(badges[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
